import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

import { UserAvatar } from '@/components/ui/UserAvatar'

export interface OrgMemberOption {
  userId: string
  name: string
  email: string
  initials: string
  avatarSeed?: string | null
}

interface MemberSearchInputProps {
  members: Array<OrgMemberOption>
  excludeUserIds: Array<string>
  onSelect: (member: OrgMemberOption) => void
  placeholder?: string
  allowExternalEmail?: boolean
  onAddExternalEmail?: (email: string) => void
  /** Label for the add/invite button. Defaults to "Invite". */
  addLabel?: string
  className?: string
}

export function MemberSearchInput({
  members,
  excludeUserIds,
  onSelect,
  placeholder = 'Enter emails, separated by commas',
  allowExternalEmail = false,
  onAddExternalEmail,
  addLabel = 'Invite',
  className,
}: MemberSearchInputProps) {
  const [query, setQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRowRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 })

  const updateDropdownPos = useCallback(() => {
    if (inputRowRef.current) {
      const rect = inputRowRef.current.getBoundingClientRect()
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width })
    }
  }, [])

  const filteredMembers = useMemo(() => {
    if (!query.trim()) return []
    const q = query.toLowerCase()
    return members.filter(
      (m) =>
        !excludeUserIds.includes(m.userId) &&
        (m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)),
    )
  }, [query, members, excludeUserIds])

  const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim())

  const canAddExternal =
    allowExternalEmail &&
    onAddExternalEmail &&
    query.trim().length > 0 &&
    filteredMembers.length === 0 &&
    isValidEmail(query)

  const handleSelect = (member: OrgMemberOption) => {
    onSelect(member)
    setQuery('')
    setShowDropdown(false)
  }

  const handleAddExternal = () => {
    if (canAddExternal && onAddExternalEmail) {
      onAddExternalEmail(query.trim())
      setQuery('')
      setShowDropdown(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (filteredMembers.length > 0) {
        handleSelect(filteredMembers[0])
      } else if (canAddExternal) {
        handleAddExternal()
      }
    }
  }

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      const target = e.target as Node
      if (
        wrapperRef.current && !wrapperRef.current.contains(target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(target))
      ) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  useEffect(() => {
    if (showDropdown && (filteredMembers.length > 0 || canAddExternal)) {
      updateDropdownPos()
    }
  }, [showDropdown, filteredMembers.length, canAddExternal, updateDropdownPos])

  const showSuggestions = showDropdown && (filteredMembers.length > 0 || canAddExternal)

  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      <div ref={inputRowRef} className="flex items-center gap-2">
        <div
          className="flex-1 flex items-center border border-muted-foreground/30 rounded-md px-3 py-2 gap-2 focus-within:border-primary transition-colors"
        >
          <input
            ref={inputRef}
            data-testid="member-search-input"
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setShowDropdown(true)
            }}
            onFocus={() => query.trim() && setShowDropdown(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none min-w-0"
          />
        </div>
        <button
          type="button"
          data-testid="member-search-add"
          onClick={() => {
            if (filteredMembers.length > 0) handleSelect(filteredMembers[0])
            else if (canAddExternal) handleAddExternal()
          }}
          disabled={filteredMembers.length === 0 && !canAddExternal}
          className={cn(
            'flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition-all flex-shrink-0',
            filteredMembers.length > 0 || canAddExternal
              ? 'bg-[hsl(var(--share-action))] text-[hsl(var(--share-action-foreground))] hover:brightness-110'
              : 'bg-[hsl(var(--share-action)_/_0.4)] text-[hsl(var(--share-action-foreground)_/_0.5)] cursor-not-allowed',
          )}
        >
          {addLabel}
        </button>
      </div>

      {showSuggestions && createPortal(
        <div
          ref={dropdownRef}
          style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
          className="z-[100] rounded-md border border-border bg-card shadow-lg max-h-48 overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-150"
        >
          {filteredMembers.map((member) => {
            return (
              <button
                key={member.userId}
                type="button"
                onClick={() => handleSelect(member)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-accent transition-colors"
              >
                <UserAvatar
                  size="xs"
                  initials={member.initials}
                  name={member.name}
                />
                <span className="text-sm text-foreground truncate">{member.name}</span>
              </button>
            )
          })}
          {canAddExternal && (
            <button
              type="button"
              onClick={handleAddExternal}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-accent transition-colors"
            >
              <span className="text-sm text-foreground">
                Invite &quot;{query.trim()}&quot;
              </span>
            </button>
          )}
        </div>,
        document.body,
      )}
    </div>
  )
}
